import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
};

// Tenants (Platform Admin)
export const tenantsAPI = {
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  suspend: (id) => api.post(`/tenants/${id}/suspend`),
  reactivate: (id) => api.post(`/tenants/${id}/reactivate`),
  assignModules: (id, modules) => api.put(`/tenants/${id}/modules`, { enabled_modules: modules }),
  getDocumentTypes: (id) => api.get(`/tenants/${id}/document-types`),
  updateDocumentTypes: (id, types) => api.put(`/tenants/${id}/document-types`, { required_document_types: types }),
  getMyDocumentTypes: () => api.get('/tenants/me/document-types'),
  createTenantAdmin: (id, data) => api.post(`/tenants/${id}/admin`, data),
  availableModules: () => api.get('/tenants/available-modules'),
  // Org structure
  listCompanies: (tenantId) => api.get(`/tenants/${tenantId}/companies`),
  createCompany: (tenantId, data) => api.post(`/tenants/${tenantId}/companies`, data),
  listFacilities: (tenantId, companyId) => api.get(`/tenants/${tenantId}/facilities`, { params: { company_id: companyId } }),
  createFacility: (tenantId, companyId, data) => api.post(`/tenants/${tenantId}/companies/${companyId}/facilities`, data),
  // User management
  listUsers: (tenantId, params) => api.get(`/tenants/${tenantId}/users`, { params }),
  createUser: (tenantId, data) => api.post(`/tenants/${tenantId}/users`, data),
  updateUser: (tenantId, userId, data) => api.put(`/tenants/${tenantId}/users/${userId}`, data),
  deactivateUser: (tenantId, userId) => api.post(`/tenants/${tenantId}/users/${userId}/deactivate`),
  activateUser: (tenantId, userId) => api.post(`/tenants/${tenantId}/users/${userId}/activate`),
};

// Documents
export const documentsAPI = {
  upload: (formData) => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (params) => api.get('/documents', { params }),
  get: (id) => api.get(`/documents/${id}`),
  getExtracted: (id) => api.get(`/documents/${id}/extracted`),
  getFile: (id) => api.get(`/documents/${id}/file`),
  delete: (id) => api.delete(`/documents/${id}`),
  getReviewQueue: (params) => api.get('/documents/review/queue', { params }),
  submitReview: (id, data) => api.post(`/documents/${id}/review`, data),
  reprocess: (id, template) => api.post(`/documents/${id}/reprocess`, null, { params: { extraction_template: template } }),
  assignReviewer: (id, reviewerId) => api.post(`/documents/${id}/assign-reviewer`, null, { params: { reviewer_id: reviewerId } }),
  listTemplates: () => api.get('/documents/templates/list'),
};

// Jobs
export const jobsAPI = {
  listOCRJobs: (params) => api.get('/jobs/ocr', { params }),
  getOCRJob: (jobId) => api.get(`/jobs/ocr/${jobId}`),
  queueOCR: (documentId) => api.post(`/jobs/ocr/${documentId}/queue`),
  retryJob: (jobId) => api.post(`/jobs/ocr/${jobId}/retry`),
  retryAllFailed: (tenantId) => api.post('/jobs/ocr/retry-all-failed', null, { params: { tenant_id: tenantId } }),
  processPending: () => api.post('/jobs/ocr/process-pending'),
  listReports: (params) => api.get('/jobs/reports', { params }),
  getReport: (reportId) => api.get(`/jobs/reports/${reportId}`),
  generatePayrollPacket: (cutoffId, facilityId) => api.post('/jobs/payroll-packet', null, { params: { cutoff_id: cutoffId, facility_id: facilityId } }),
  generateClientSummary: (clientId, startDate, endDate) => api.post('/jobs/client-summary', null, { params: { client_id: clientId, start_date: startDate, end_date: endDate } }),
};

// Timesheet
export const timesheetAPI = {
  createEntry: (data) => api.post('/timesheet/entries', data),
  listEntries: (params) => api.get('/timesheet/entries', { params }),
  getEntry: (id) => api.get(`/timesheet/entries/${id}`),
  updateEntry: (id, data) => api.put(`/timesheet/entries/${id}`, data),
  verifyEntry: (id) => api.post(`/timesheet/entries/${id}/verify`),
  listCutoffs: (params) => api.get('/timesheet/cutoffs', { params }),
  createCutoff: (data) => api.post('/timesheet/cutoffs', data),
  getCutoff: (id) => api.get(`/timesheet/cutoffs/${id}`),
  processCutoff: (id) => api.post(`/timesheet/cutoffs/${id}/process`),
  generatePacket: (cutoffId, facilityId, inclusionMode) =>
    api.post(`/timesheet/cutoffs/${cutoffId}/generate-packet`, null, { params: { facility_id: facilityId, inclusion_mode: inclusionMode } }),
  listPacketJobs: (params) => api.get('/timesheet/packet-jobs', { params }),
  getMyTimesheets: (params) => api.get('/timesheet/my-timesheets', { params }),
  getFacilitySummary: (params) => api.get('/timesheet/facility-summary', { params }),
};

// Intake
export const intakeAPI = {
  createClient: (data) => api.post('/intake/clients', data),
  listClients: (params) => api.get('/intake/clients', { params }),
  getClient: (id) => api.get(`/intake/clients/${id}`),
  updateClient: (id, data) => api.put(`/intake/clients/${id}`, data),
  getClientWorkspace: (id) => api.get(`/intake/clients/${id}/workspace`),
  getPortalInfo: (slug) => api.get(`/intake/portal/${slug}`),
  portalUpload: (slug, formData) => api.post(`/intake/portal/${slug}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listSubmissions: (params) => api.get('/intake/submissions', { params }),
  getSubmission: (id) => api.get(`/intake/submissions/${id}`),
  reviewSubmission: (id, data) => api.post(`/intake/submissions/${id}/review`, data),
  getClientSummary: (clientId, params) => api.get(`/intake/clients/${clientId}/summary`, { params }),
  // Email intake
  ingestEmail: (emailData) => api.post('/intake/email-ingest', emailData),
  listEmailIngestions: (params) => api.get('/intake/email-ingestions', { params }),
  // Deliverables
  uploadDeliverable: (clientId, formData) => api.post(`/intake/clients/${clientId}/deliverables`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listDeliverables: (clientId, params) => api.get(`/intake/clients/${clientId}/deliverables`, { params }),
  downloadDeliverable: (clientId, deliverableId) => api.get(`/intake/clients/${clientId}/deliverables/${deliverableId}/download`),
  deleteDeliverable: (clientId, deliverableId) => api.delete(`/intake/clients/${clientId}/deliverables/${deliverableId}`),
  getDeliverableTypes: () => api.get('/intake/deliverable-types'),
};

// Dashboard
export const dashboardAPI = {
  getStats: (tenantId) => api.get('/dashboard/stats', { params: { tenant_id: tenantId } }),
  getAnalytics: (tenantId, days = 7) => api.get('/dashboard/analytics', { params: { tenant_id: tenantId, days } }),
  getRecentActivity: (tenantId, limit) => api.get('/dashboard/recent-activity', { params: { tenant_id: tenantId, limit } }),
  getModuleStats: (tenantId) => api.get('/dashboard/module-stats', { params: { tenant_id: tenantId } }),
  getOCRQueueStats: (tenantId) => api.get('/dashboard/ocr-queue', { params: { tenant_id: tenantId } }),
};

// Search
export const searchAPI = {
  search: (params) => api.get('/search', { params }),
  suggestions: (params) => api.get('/search/suggestions', { params }),
  reindex: (tenantId) => api.post('/search/reindex', null, { params: { tenant_id: tenantId } }),
};

// Notifications
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  getCount: (tenantId) => api.get('/notifications/count', { params: { tenant_id: tenantId } }),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllRead: (tenantId) => api.post('/notifications/read-all', null, { params: { tenant_id: tenantId } }),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Contracts
export const contractsAPI = {
  list: (params) => api.get('/contracts', { params }),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  stats: () => api.get('/contracts/stats'),
  upload: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/contracts/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  download: (id) => api.get(`/contracts/${id}/download`, { responseType: 'blob' }),
  checkExpirations: () => api.post('/contracts/check-expirations'),
};

// Audit Logs
export const auditLogsAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  getSummary: (tenantId, days = 7) => api.get('/audit-logs/summary', { params: { tenant_id: tenantId, days } }),
  getActions: (tenantId) => api.get('/audit-logs/actions', { params: { tenant_id: tenantId } }),
};

export default api;
